import { useEffect, useState, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "./lib/api"; // Use configured axios
import { initializeOrganization, clearAuthContext, getActiveOrgRole } from "./lib/api";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import PlatformView from "./pages/PlatformView";
import Integrations from "./pages/Integrations";
import Alerts from "./pages/Alerts";
import TrackingSetup from "./pages/TrackingSetup";
import Profile from "./pages/Profile";
import Compare from "./pages/Compare";
import AnalyticsPage from "./pages/AnalyticsPage";
import AnalyticsBrandsPage from "./pages/AnalyticsBrandsPage";
import AnalyticsProductsPage from "./pages/AnalyticsProductsPage";
import AnalyticsStockPage from "./pages/AnalyticsStockPage";
import GoogleAdsProductsPage from "./pages/GoogleAdsProductsPage";
import MetaAdsProductsPage from "./pages/MetaAdsProductsPage";
import FeedOptimizerPage from "./pages/FeedOptimizerPage";
import CatalogFeedsPage from "./pages/CatalogFeedsPage";
import AnalyticsCustomerLTVPage from "./pages/AnalyticsCustomerLTVPage";
import FunnelOpportunitiesPage from "./pages/FunnelOpportunitiesPage";
import AwinTransactionsPage from "./pages/AwinTransactionsPage";
import FixedCostsPage from "./pages/FixedCostsPage";
import FinancialYearPage from "./pages/FinancialYearPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import BillingPage from "./pages/BillingPage";
import Paywall from "./pages/Paywall";
import AdminLayout from "./components/AdminLayout";
import AdminOverview from "./pages/AdminOverview";
import AdminOrganizations from "./pages/AdminOrganizations";
import AdminOrgDetail from "./pages/AdminOrgDetail";
import AdminUsers from "./pages/AdminUsers";
import AdminSyncLogs from "./pages/AdminSyncLogs";
import AdminPlans from "./pages/AdminPlans";
import AdminEmailPage from "./pages/AdminEmailPage";
import ImpersonationBanner from "./components/ImpersonationBanner";
import TelegramNotificationsPage from "./pages/TelegramNotificationsPage";
import TeamPage from "./pages/TeamPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

/**
 * OptionalAuthRoute — wraps a public page that can optionally use auth context.
 * Does NOT redirect if unauthenticated; just provides user=null.
 */
const OptionalAuthRoute = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('auth_user_cache');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);

    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];

      axios
        .post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        )
        .then(async (response) => {
          const user = response.data;
          // Clear any stale org from a previous session, then init for this user
          clearAuthContext();
          const orgResult = await initializeOrganization(user?.user_id);
          navigate("/dashboard", { state: { user, orgRole: orgResult?.role || null } }, { replace: true });
        })
        .catch((error) => {
          console.error("Auth error:", error);
          navigate("/login", { replace: true });
        });
    } else {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
        <p className="text-slate-600">Autenticando...</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children, requireSubscription = false }) => {
  // Hydrate user synchronously from cache to avoid flash of empty state on navigation
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('auth_user_cache');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  // Hydrate orgRole from localStorage cache
  const [orgRole, setOrgRole] = useState(() => {
    try {
      const uid = localStorage.getItem('active_user_id');
      return uid ? (localStorage.getItem(`active_org_role:${uid}`) || null) : null;
    } catch { return null; }
  });
  // If we have both a token and a cached user, assume authenticated while we verify in background
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return (localStorage.getItem('auth_token') && localStorage.getItem('auth_user_cache')) ? true : null;
    } catch { return null; }
  });
  const [subscriptionOk, setSubscriptionOk] = useState(() => {
    if (!requireSubscription) return true;
    try {
      // Only restore cached 'true' to avoid phantom paywalls; admin bypass handled below
      const cachedUser = localStorage.getItem('auth_user_cache');
      const parsedUser = cachedUser ? JSON.parse(cachedUser) : null;
      if (parsedUser?.role === "super_admin" || parsedUser?.role === "support_admin") return true;
      return localStorage.getItem('subscription_ok_cache') === 'true' ? true : null;
    } catch { return null; }
  });
  const location = useLocation();
  const navigate = useNavigate();

  const checkSubscription = useCallback(async (userData) => {
    if (!requireSubscription) {
      setSubscriptionOk(true);
      return;
    }
    // Admin users bypass the paywall
    if (userData?.role === "super_admin" || userData?.role === "support_admin") {
      setSubscriptionOk(true);
      return;
    }
    try {
      const storedToken = localStorage.getItem("auth_token");
      const subRes = await axios.get(`${API}/billing/subscription-status`, {
        headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {},
        withCredentials: true
      });
      const sub = subRes.data;
      const hasAccess = sub.subscription_status === "active" || sub.subscription_status === "trialing" || sub.is_grandfathered || !!sub.plan_override;
      setSubscriptionOk(hasAccess);
      try { localStorage.setItem('subscription_ok_cache', String(hasAccess)); } catch {}
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403 || status === 402) {
        // Explicit subscription denial
        try { localStorage.removeItem('subscription_ok_cache'); } catch {}
        setSubscriptionOk(false);
      } else {
        // Network error or 5xx — assume still subscribed (don't lock user out on transient errors)
        setSubscriptionOk(true);
      }
    }
  }, [requireSubscription]);

  useEffect(() => {
    // Fast path: user passed from registration/OAuth callback
    if (location.state?.user) {
      const userData = location.state.user;
      try { localStorage.setItem('auth_user_cache', JSON.stringify(userData)); } catch {}
      setUser(userData);
      setIsAuthenticated(true);
      // Fetch org + role fresh (async, fire-and-forget)
      initializeOrganization(userData?.user_id).then(orgResult => {
        if (orgResult?.role) setOrgRole(orgResult.role);
      });
      checkSubscription(userData);
      return;
    }

    const checkAuth = async () => {
      const storedToken = localStorage.getItem("auth_token");
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          timeout: 8000,
          headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {}
        });
        const userData = response.data;
        // Update localStorage cache with fresh data
        try { localStorage.setItem('auth_user_cache', JSON.stringify(userData)); } catch {}
        setUser(userData);
        setIsAuthenticated(true);
        const orgResult = await initializeOrganization(userData?.user_id);
        if (orgResult?.role) setOrgRole(orgResult.role);
        await checkSubscription(userData);
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401) {
          // Token genuinely invalid/expired → clear cache and redirect to login
          try { localStorage.removeItem('auth_user_cache'); } catch {}
          setIsAuthenticated(false);
          navigate("/login", { replace: true });
        } else {
          // Network error or 5xx — backend temporarily unavailable (restart, heavy sync, etc.)
          // Keep user logged in with cached data: DO NOT call setUser(null)
          if (storedToken) {
            setIsAuthenticated(true);
            // user state already hydrated from localStorage cache in useState initializer
            // just ensure subscriptionOk is resolved so UI doesn't stay in loading state
            await checkSubscription(user);
          } else {
            setIsAuthenticated(false);
            navigate("/login", { replace: true });
          }
        }
      }
    };

    checkAuth();
  }, [location, navigate, checkSubscription]);

  if (isAuthenticated === null || (requireSubscription && subscriptionOk === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // If subscription required but not active, show paywall
  if (requireSubscription && !subscriptionOk) {
    return (
      <AuthContext.Provider value={{ user, setUser, orgRole, setOrgRole }}>
        <Paywall />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, orgRole, setOrgRole }}>
      {children}
    </AuthContext.Provider>
  );
};

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (from OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireSubscription>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform/:platform"
        element={
          <ProtectedRoute requireSubscription>
            <PlatformView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <Integrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/integrations"
        element={
          <ProtectedRoute>
            <Integrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute requireSubscription>
            <Alerts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tracking"
        element={
          <ProtectedRoute>
            <TrackingSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare"
        element={
          <ProtectedRoute requireSubscription>
            <Compare />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute requireSubscription>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/brands"
        element={
          <ProtectedRoute requireSubscription>
            <AnalyticsBrandsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/products"
        element={
          <ProtectedRoute requireSubscription>
            <AnalyticsProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/stock"
        element={
          <ProtectedRoute requireSubscription>
            <AnalyticsStockPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/google-ads-products"
        element={
          <ProtectedRoute requireSubscription>
            <GoogleAdsProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/meta-ads-products"
        element={
          <ProtectedRoute requireSubscription>
            <MetaAdsProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/feed-optimizer"
        element={
          <ProtectedRoute requireSubscription>
            <FeedOptimizerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed-datos"
        element={
          <ProtectedRoute requireSubscription>
            <CatalogFeedsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/customer-ltv"
        element={
          <ProtectedRoute requireSubscription>
            <AnalyticsCustomerLTVPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/profit-simulator"
        element={
          <ProtectedRoute requireSubscription>
            <FunnelOpportunitiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/funnel-opportunities"
        element={
          <ProtectedRoute requireSubscription>
            <FunnelOpportunitiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform/awin/transactions"
        element={
          <ProtectedRoute requireSubscription>
            <AwinTransactionsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/politica-de-privacidad" element={<PrivacyPolicy />} />
      <Route path="/terminos-y-condiciones" element={<TermsConditions />} />
      <Route path="/fixed-costs" element={<ProtectedRoute requireSubscription><FixedCostsPage /></ProtectedRoute>} />
      <Route path="/finance/annual" element={<ProtectedRoute requireSubscription><FinancialYearPage /></ProtectedRoute>} />
      <Route path="/notifications/telegram" element={<ProtectedRoute><TelegramNotificationsPage /></ProtectedRoute>} />
      <Route path="/settings/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/invite/accept" element={<OptionalAuthRoute><InviteAcceptPage /></OptionalAuthRoute>} />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminOverview />} />
        <Route path="organizations" element={<AdminOrganizations />} />
        <Route path="organizations/:orgId" element={<AdminOrgDetail />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="sync-logs" element={<AdminSyncLogs />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="email" element={<AdminEmailPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ImpersonationBanner />
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
